import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { BaseEntity, BaseState } from '@base/entities/BaseEntities';
import { AppConfigService } from '@configuration/app.config.service';
import { ScreenService } from '@core/framework/screen.service';
import { EnumApplicationMode, EnumPageDisplayModes, EnumScreenSizes, EnumWindowSizes, SysEnumsService } from '@core/framework/sysenums.enum';
import { ToolbarSettings } from '@m4u-angular/base/classes/BaseClasses';
import { BasePageCRUDComponent } from '@m4u-angular/base/component/BasePageCRUDComponent';
import { FrameworkService } from '@m4u-angular/core/framework/framework.service';
import { SysEventsService } from '@m4u-angular/core/framework/sysevents.service';
import { DxContextMenuComponent, DxPopupComponent, DxScrollViewComponent } from 'devextreme-angular';
import { Subscription } from 'rxjs';

@Component({
    selector: 'm4u-page',
    templateUrl: './pagelayout.component.html',
    styleUrls: ['./pagelayout.component.scss', '../page-filter/page-filter.component.scss']
})
export class PageLayoutComponent implements OnInit, OnDestroy {

    @HostListener('document:keydown.escape', ['$event'])
    EscPress = (event: KeyboardEvent) => this.EscPressed(event);

    private _isPageButtonsVisible: boolean;
    private _pageToolbarSettings: ToolbarSettings;
    private _subscriptions: Subscription[] = [];

    IsPageSidePanelVisible: boolean;
    ShowPreview: boolean;
    PreviewPDFData: string | Blob;
    PreviewFileName: string;

    @ViewChild('pageHeader') pageHeader: ElementRef;
    @ViewChild('popup') popup: DxPopupComponent;
    @ViewChild('scroll') scroll: DxScrollViewComponent;
    @ViewChild('mnuViewModes') protected viewModesMenu: DxContextMenuComponent;

    constructor(public screen: ScreenService,
        private appConfigService: AppConfigService,
        public SysEnums: SysEnumsService,
        private sysEvents: SysEventsService,
        public screenService: ScreenService,
        private frameworkService: FrameworkService) {
    }

    @Input() public WindowHeight: EnumWindowSizes;
    @Input() public WindowWidth: EnumWindowSizes;
    @Input() public IsNoToolbar: boolean;
    @Input() public IsNoBodyScroll: boolean;
    @Input() public Class: string;
    @Input() public ShowScrollbar: string;
    @Input() ButtonsPosition: 'toolbar' | 'header' | 'header-title';
    @Input() Page: BasePageComponent<BaseEntity, BaseState>;
    @Input() public ShowDialogAsWindow: boolean;
    @Input() public IsFooterHidden: boolean;
    @Input() public IsHeaderInBody: boolean;
    @Input() public IsHeaderCollapsed: boolean;

    @Input() SidePanelTemplate: TemplateRef<any>;
    @Input() ToolbarButtonsBefore: TemplateRef<any>;
    @Input() ToolbarButtonsCenter: TemplateRef<any>;
    @Input() ToolbarButtonsAfter: TemplateRef<any>;
    @Input() QuickFilters: TemplateRef<any>;
    @Input() HeaderButtonsBefore: TemplateRef<any>;
    @Input() HeaderButtonsAfter: TemplateRef<any>;
    @Input() TitleButtons: TemplateRef<any>;
    //    
    @Input() PageBody: TemplateRef<any>;
    @Input() SidePanelPosition: 'left' | 'right';
    @Input() HideSidePanelToggle: boolean;
    @Input() HidePageBreadcrumb: boolean = false;
    @Input() HideToolbarBreadcrumbs: boolean = false;
    @Input() DisableFilterApply: boolean = false;

    get ShowBreadBrumb() {
        return this.appConfigService.AppConfig.ShowBreadcrumb() && !this.HidePageBreadcrumb;
    }

    get Title() {
        return this._pageToolbarSettings?.Title;
    }

    get Subtitle() {
        return this._pageToolbarSettings?.Subtitle;
    }

    IsShowViewModesButton() {
        return this._pageToolbarSettings?.ViewModes && this._pageToolbarSettings?.ViewModes.length;
    }

    get ViewModes() {
        return this._pageToolbarSettings?.ViewModes;
    }

    get ShowBackButton() {
        return this._pageToolbarSettings?.ShowBackButton;
    }

    ngOnInit() {
        this._subscriptions.push(this.sysEvents.Toolbar.subscribe((toolbarSettings: ToolbarSettings) => {
            if (!toolbarSettings || this.Page.ComponentName !== toolbarSettings.PageComponentName) return;

            this._pageToolbarSettings = toolbarSettings;

            if (!this.ButtonsPosition) {
                this.ButtonsPosition = this.Page.DisplayMode == EnumPageDisplayModes.Window
                    || this.Page.DisplayMode == EnumPageDisplayModes.Dialog
                    || this.Page instanceof BasePageCRUDComponent
                    ? 'toolbar'
                    : this.screen.IsDesktop() ? 'header' : 'header-title';
            }

            const contextMenuItems = toolbarSettings.OnGetContextMenuItems();
            this._isPageButtonsVisible = toolbarSettings.IsAllowFilter || toolbarSettings.IsAllowPrint || toolbarSettings.IsAllowExport
                || (contextMenuItems && contextMenuItems.length > 0);
        }));

        this._subscriptions.push(this.sysEvents.PageSidePanelState.subscribe(({ pageComponentName, state }) => {
            if (pageComponentName != this.Page.ComponentName) return;

            this.IsPageSidePanelVisible = state;
        }));

        this._subscriptions.push(this.sysEvents.PagePreviewState.subscribe(({ pageComponentName, state, data, fileName }) => {
            if (pageComponentName != this.Page.ComponentName) return;

            this.ShowPreview = state;
            this.PreviewPDFData = data;
            this.PreviewFileName = fileName;
        }));
    }

    GetPreviewTitle() {
        return this._pageToolbarSettings.OnGetPreviewTitle();
    }

    ngOnDestroy() {
        this._subscriptions && this._subscriptions.forEach(x => x.unsubscribe());
    }

    IsHeaderEmpty() {
        return this.IsQuickFiltersEmpty && !this.HasContent(this.pageHeader) && !(this.IsMobileTitleVisible());
    }

    IsMobileTitleVisible() {
        return this.IsStandardApplicationMode() && this.screen.IsMobile() && this.Page.DisplayMode === EnumPageDisplayModes.FullScreen;
    }

    get IsQuickFiltersEmpty() {
        return !this.QuickFilters && !this.HeaderButtonsBefore && !this.HeaderButtonsAfter && 
            (this.ButtonsPosition === 'toolbar' || this.ButtonsPosition === 'header-title' || !this._isPageButtonsVisible);
    }

    GetPageLoadingClass() {
        return this.Page.IsLoading ? 'page-is-loading' : '';
    }

    GetCustomClass() {
        return (this.Class || '') + ` ${this.Page.ComponentName}` + ` ${this.GetPageLoadingClass()}`;
    }

    GetDialogClass() {
        // eslint-disable-next-line max-len
        return `m4u-popup ${this.GetPopupWidthClass()} ${this.GetCustomClass()} ${this.Page.ShowDialogAsBottomSheet ? 'm4u-bottom-sheet' : 'm4u-dialog'}`;
    }

    GetDialogWidth() {
        return this.Page.ShowDialogAsBottomSheet ? '100%' : this.GetPopupSize(this.WindowWidth);
    }

    GetDialogHeight() {
        const height = this.Page.ShowDialogAsBottomSheet ? this.GetBottomSheetHeight(this.WindowHeight) : this.ShowDialogAsWindow
            ? '100%' : this.GetPopupSize(this.WindowHeight);

        const currentHeight = this.popup?.instance?.option('height');
        if (currentHeight === height) return currentHeight;

        this.popup?.instance?.option('height', height);
        return height;
    }

    GetDialogPosition() {
        return this.Page.ShowDialogAsBottomSheet ? 'bottom' : this.ShowDialogAsWindow ? this.GetPopupPosition() : 'center';
    }

    GetPopupSize(size: EnumWindowSizes) {
        if (this.IsShowAsFullScreen()) return '100%';

        switch (size) {
            case EnumWindowSizes.WindowXSmall: return '30%';
            case EnumWindowSizes.WindowSmall: return '40%';
            case EnumWindowSizes.WindowMedium: return '50%';
            case EnumWindowSizes.WindowLarge: return '75%';
            case EnumWindowSizes.WindowXLarge: return '90%';
            case EnumWindowSizes.WindowAutoSize: return this.WindowHeight == size ? 'auto' : '100%';
        }
    }

    GetBottomSheetHeight(size: EnumWindowSizes) {
        if (this.IsShowAsFullScreen()) return '95dvh';

        switch (size) {
            case EnumWindowSizes.WindowXSmall: return '50dvh';
            case EnumWindowSizes.WindowSmall: return '60dvh';
            case EnumWindowSizes.WindowMedium: return '70dvh';
            case EnumWindowSizes.WindowLarge: return '80dvh';
            case EnumWindowSizes.WindowXLarge: return '95dvh';
            case EnumWindowSizes.WindowAutoSize: return 'auto';
        }
    }

    GetWindowWidth(size: EnumWindowSizes) {
        if (this.IsShowAsFullScreen()) return '100%';

        const previewSize = this.ShowPreview ? this.GetPreviewSize(size) : '0px';
        switch (size) {
            case EnumWindowSizes.WindowXSmall: return `calc(${previewSize} + 30vw)`;
            case EnumWindowSizes.WindowSmall: return `calc(${previewSize} + 40vw)`;
            case EnumWindowSizes.WindowMedium: return `calc(${previewSize} + 50vw)`;
            case EnumWindowSizes.WindowLarge: return `calc(${previewSize} + 75vw)`;
            case EnumWindowSizes.WindowXLarge: return `calc(${previewSize} + 90vw)`;
            case EnumWindowSizes.WindowAutoSize: return '100%';
        }
    }

    GetPreviewSize(size: EnumWindowSizes) {
        switch (size) {
            case EnumWindowSizes.WindowXSmall: return '45vw';
            case EnumWindowSizes.WindowSmall: return '45vw';
            case EnumWindowSizes.WindowMedium: return '45vw';
            case EnumWindowSizes.WindowLarge: return '20vw';
            case EnumWindowSizes.WindowXLarge: return '10vw';
            case EnumWindowSizes.WindowAutoSize: return 0;
        }
    }

    GetPopupWidthClass() {
        switch (this.WindowWidth) {
            case EnumWindowSizes.WindowXSmall:
            case EnumWindowSizes.WindowSmall:
                return 'm4u-popup-small';
            case EnumWindowSizes.WindowMedium:
                return 'm4u-popup-medium';
            case EnumWindowSizes.WindowLarge:
            case EnumWindowSizes.WindowXLarge:
                return 'm4u-popup-large';
            case EnumWindowSizes.WindowAutoSize:
                return 'm4u-popup-auto';
        }
    }

    GetPopupPosition() {
        return this.screen.IsRTL() ? 'left top' : 'right top';
    }

    GetBottomSheetPosition() {
        return this.screen.IsRTL() ? 'right top' : 'left top';
    }

    GetPopupShowAnimation() {
        const direction = this.screen.IsRTL() ? 'left' : 'right';
        return { type: 'slideIn', direction: direction, duration: 150 };
    }

    GetPopupHideAnimation() {
        const direction = this.screen.IsRTL() ? 'left' : 'right';
        return { type: 'slideOut', direction: direction, duration: 150 };
    }

    GetBottomSheetShowAnimation() {
        return { type: 'slideIn', direction: 'bottom', duration: 150 };
    }

    GetBottomSheetHideAnimation() {
        return { type: 'slideOut', direction: 'bottom', duration: 150 };
    }

    //Returns whether current page should be shown as full screen
    //This is a major hub function that determines visibility of window and dialog (not so relevant to DisplayMode=FullScreen)
    //Basically:
    //DisplayMode=FullScreen - always rendered on full screen
    //DisplayMode=Window - rendered on full screen if screen is XSmall
    //DisplayMode=Dialog - rendered on full screen if screen is XSmall and the page itself is not of small dimensions.
    public IsShowAsFullScreen(): boolean {

        //Screen and current page sizes
        const bMobile: boolean = this.screen.IsMobile();
        const bSmallPage: boolean = (this.WindowHeight == EnumWindowSizes.WindowXSmall
            || this.WindowHeight == EnumWindowSizes.WindowSmall
            || this.WindowHeight == EnumWindowSizes.WindowAutoSize) && (this.WindowWidth == EnumWindowSizes.WindowXSmall);

        //No need for special classes if showing full screen, unless this is a mobile device
        if (this.Page.DisplayMode == EnumPageDisplayModes.FullScreen) return true;

        //For window - show full screen or in window according to screen size. Use window dimensions
        if (this.Page.DisplayMode == EnumPageDisplayModes.Window) return bMobile ? true : false;

        //For dialog - show always in partial view, unless this is a mobile device and the page is large
        if (this.Page.DisplayMode == EnumPageDisplayModes.Dialog) return false;//bMobile ? (bSmallPage ? false : true) : false;
    }

    public IsBodyWithScroll() {
        return !this.IsNoBodyScroll && (this.screen.IsDesktop() || this.screen.IsTablet() || this.Page.DisplayMode === EnumPageDisplayModes.Dialog);
    }

    OnPopupHiding(e) {
        e.cancel = true;
        this.Page.NavigateBack();
    }

    //Specify whether to render the toolbar
    public IsRenderToolbar(): boolean {
        return !this.IsNoToolbar && this.appConfigService.AppConfig.Mode !== EnumApplicationMode.IFrame;
    }

    IsStandardApplicationMode() {
        return this.appConfigService.AppConfig.Mode === EnumApplicationMode.Standard;
    }

    public IsShowNavbar() {
        return (this.Page.DisplayMode !== 'Dialog' && !(this.Page instanceof BasePageCRUDComponent) && this.screen.IsMobile())
            || ((this.Page as any).IsAllowNavbar && (this.Page as any).IsAllowNavbar());
    }

    OnSidePanelToggle() {
        this.IsPageSidePanelVisible = !this.IsPageSidePanelVisible;
        this.sysEvents.SetPageSidePanelState(this.Page.ComponentName, this.IsPageSidePanelVisible);
    }

    HasContent(content: any) {
        return content
            && (content.childElementCount !== undefined ? content.childElementCount > 0 : content.nativeElement.childElementCount > 0);
    }

    OnNavigateHomeOrSwitchMode() {
        if (this.IsShowViewModesButton()) {
            //Switch mode
            this.OnShowViewModes();
        } else {
            //Go to home route
            if (!this.IsStandardApplicationMode() || !this._pageToolbarSettings?.Page 
                || this._pageToolbarSettings?.Page.DisplayMode != EnumPageDisplayModes.FullScreen) return;
            this.frameworkService.NavigateDefault();
        }
    }

    OnViewModesMenuClick(e) {
        this._pageToolbarSettings.Title = e.itemData.text;
        e.itemData.callback();
    }

    OnShowViewModes = () => {
        this.viewModesMenu.instance.show();
    };

    OnBack() {
        this._pageToolbarSettings.BackButtonClick();
    }

    EscPressed = (event: KeyboardEvent) => {
        const popupElement = this.GetPopupElement(event?.target as any);
        if (!popupElement?.classList?.contains(this.Page?.ComponentName)) return;

        if (this.Page?.DisplayMode === EnumPageDisplayModes.Window || this.Page?.DisplayMode === EnumPageDisplayModes.Dialog) {
            this.Page.ClosePage();
        }
    };

    private GetPopupElement(el: Element) {
        if (!el) return null;
        if (el.classList.contains('m4u-popup')) return el;

        return this.GetPopupElement(el.parentElement);
    }

    OnDialogShown() {
        this.sysEvents.DialogShown(this.Page.ComponentName);
    }

    // experimental
    // GetPopupSize(size: EnumWindowSizes) {
    //     if (this.IsShowAsFullScreen()) return '100%';

    //     const screenSize = this.screen.GetScreenSize();
    //     //
    //     console.log(screenSize);
    //     //
    //     if (screenSize === EnumScreenSizes.ScreenXSmall) return '90%';

    //     switch (size) {
    //         case EnumWindowSizes.WindowXSmall:
    //             return (screenSize === EnumScreenSizes.ScreenSmall)
    //                 ? '60%'
    //                 : (screenSize === EnumScreenSizes.ScreenMedium) ? '50%' : '40%';
    //         case EnumWindowSizes.WindowSmall:
    //             return (screenSize === EnumScreenSizes.ScreenSmall)
    //                 ? '55%'
    //                 : (screenSize === EnumScreenSizes.ScreenMedium) ? '50%' : '5%';
    //         case EnumWindowSizes.WindowMedium:
    //             return (screenSize === EnumScreenSizes.ScreenSmall)
    //             ? '70%'
    //             : (screenSize === EnumScreenSizes.ScreenMedium) ? '60%' : '50%';
    //         case EnumWindowSizes.WindowLarge: return '75%';
    //         case EnumWindowSizes.WindowXLarge: return '90%';
    //         case EnumWindowSizes.WindowAutoSize:

    //             switch (screenSize) {
    //                 case EnumScreenSizes.ScreenSmall:
    //                     return '70%';
    //                 case EnumScreenSizes.ScreenMedium:
    //                     return '60%';
    //                 case EnumScreenSizes.ScreenLarge:
    //                     return '50%';
    //                 case EnumScreenSizes.ScreenXLarge:
    //                     return '40%';
    //             }

    //         //return this.WindowHeight == size ? 'auto' : '100%';
    //     }
    // }
}
